import { useGSAP } from '@gsap/react';
import gsap from 'gsap';

import { graphql, useStaticQuery } from 'gatsby';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiFillInfoCircle } from 'react-icons/ai';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import 'react-multi-carousel/lib/styles.css';
import { ALL_LANGS } from '../../../constants';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import areaIcon from '../../../images/areaIcon.svg';
import { AlternativeFund } from '../../../templates/AlternativeFund';
import { BackgroundColorTheme, ModuleBgColor } from '../../../templates/Page';
import { getPageDocumentUrl, localize } from '../../../utils/sanity';
import { clsx } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import CenteredCarousel from '../CenteredCarousel';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import SmartLink from '../SmartLink';
import TextLink from '../TextLink';
import * as styles from './AlternativeFundAssetCarouselModule.module.scss';

export const AlternativeFundAssetCarouselModuleFragment = graphql`
  fragment AlternativeFundAssetCarouselModule on SanityPageAlternativeFundAssetCarouselModule {
    title {
      ...LocaleStringFragment
    }
    assetsToUse
    assetsReferences {
      ...AlternativeFundAsset
    }
    backgroundColor
  }
`;

export interface AlternativeFundAsset {
  title: string;
  description: string;
  fund: AlternativeFund;
  location: string;
  totalArea: number;
  objective: {
    title: string;
  };
  image: SanityImageType;
}

export type AlternativeFundAssetCarouselModuleProps = {
  title: string;
  backgroundColor: BackgroundColorTheme | '#FFFFFF';
  className?: string;
  noLink?: boolean;
} & (
  | {
      assetsToUse: 'all';
      assetsReferences?: never;
    }
  | {
      assetsToUse: 'chooseManually';
      assetsReferences: Array<AlternativeFundAsset>;
    }
);

interface QueryData {
  allSanityAlternativeFundAsset: {
    nodes: Array<AlternativeFundAsset>;
  };
}

const ButtonGroup = ({
  next,
  previous,
  carouselState: { currentSlide, totalItems, slidesToShow },
}: {
  next: () => void;
  previous: () => void;
  carouselState: { currentSlide: number; totalItems: number; slidesToShow: number };
}) => {
  return (
    <div className={clsx(styles.buttonGroup, totalItems < 2 && styles.noArrows)}>
      <button
        disabled={currentSlide === 0}
        className={clsx(styles.button, styles.buttonLeft)}
        onClick={() => {
          previous();
        }}
      >
        <FaChevronLeft className={styles.buttonIcon} />
        <div className={styles.visuallyHidden}>Previous</div>
      </button>

      <button
        disabled={currentSlide === totalItems - slidesToShow}
        className={clsx(styles.button, styles.buttonRight)}
        onClick={() => {
          next();
        }}
      >
        <FaChevronRight className={styles.buttonIcon} />
        <div className={styles.visuallyHidden}>Next</div>
      </button>
    </div>
  );
};

export function getModuleBgColor(props: AlternativeFundAssetCarouselModuleProps): ModuleBgColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */
  return props.backgroundColor === '#FFFFFF' ? 'white' : 'colored';
}

function AlternativeFundAssetCarouselModule(
  props: AlternativeFundAssetCarouselModuleProps & CommonModuleProps,
): React.ReactElement {
  const queryData = useStaticQuery<QueryData>(graphql`
    {
      allSanityAlternativeFundAsset(sort: { orderRank: ASC }) {
        nodes {
          ...AlternativeFundAsset
        }
      }
    }
  `);

  const {
    title,
    assetsToUse,
    assetsReferences,
    previousModuleBgColor,
    backgroundColor,
    className,
    noLink,
    moduleId,
  } = props;

  const { i18n, t } = useTranslation();

  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: QueryData = localize(queryData, langs);

  const { allSanityAlternativeFundAsset } = localizedData;

  const containerRef = useRef<HTMLElement | null>(null);

  const [fundsRendered, setFundsRendered] = useState(false);

  const assetsToShow =
    assetsToUse === 'chooseManually' ? assetsReferences : allSanityAlternativeFundAsset.nodes;

  useGSAP(
    () => {
      if (!fundsRendered || assetsToShow.length < 1) {
        return; // avoid errors on console
      }
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: `.${styles.contentContainer}`,
          start: 'top 85%',
        } satisfies ScrollTrigger.Vars,
      });

      tl.from(`.${styles.assetLink}`, {
        y: 100,
        autoAlpha: 0,
        stagger: 0.2,
        duration: 0.6,
        clearProps: 'transform',
      });
    },
    { scope: containerRef, dependencies: [fundsRendered] },
  );

  function renderCard(asset: AlternativeFundAsset) {
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-PT', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      });
    };

    return (
      <SmartLink
        to={getPageDocumentUrl({
          __typename: 'SanityAlternativeFund',
          slug: asset.fund.slug,
        })}
        className={clsx(styles.assetLink, noLink && styles.noLink)}
      >
        <div className={styles.assetContainer}>
          <div className={styles.imageContainer}>
            <Image image={asset.image} />
            <div className={styles.overlay}></div>
            <div className={styles.titleContainer}>
              <span className={styles.fundTitle}>{asset.fund.name}</span>
              <h3 className={styles.title}>{asset.title}</h3>
            </div>
          </div>
          <div className={clsx(styles.characteristicsContainer, styles.characteristicsBar)}>
            <div className={styles.characteristicContainer}>
              <AiFillInfoCircle className={styles.characteristicIcon} />
              <p className={styles.characteristic}>{asset.objective.title}</p>
            </div>
            <div className={styles.characteristicContainer}>
              <img src={areaIcon} alt="" className={styles.characteristicIcon} />
              <p className={styles.characteristic}>{asset.totalArea}m2</p>
            </div>
          </div>
          <div className={styles.textContainer}>
            <div className={styles.characteristicsContainer}>
              {/* <div className={clsx(styles.characteristicContainer)}>
                <span className={styles.characteristicTitle}>
                  {t('fund_card.aquisition_date', 'Data de aquisição')}
                </span>
                <p className={styles.characteristic}>{formatDate(asset.aquisitionDate)}</p>
              </div> */}
              <div className={styles.characteristicContainer}>
                <span className={styles.characteristicTitle}>
                  {t('fund_card.location', 'Localização')}
                </span>
                <p className={styles.characteristic}>{asset.location}</p>
              </div>
            </div>
            <p className={styles.description}>{asset.description}</p>
            {!noLink && (
              <TextLink styleOnly className={styles.link} withArrowIcon>
                {t('fund_card.see_fund_link', 'Ver fundo')}
              </TextLink>
            )}
          </div>
        </div>
      </SmartLink>
    );
  }

  return assetsToShow.length > 0 ? (
    <ModuleLayout
      id={moduleId}
      className={clsx(className, styles.container)}
      contentClassName={clsx(
        styles.contentContainer,

        backgroundColor === '#FFFFFF' && styles.whiteBackground,

        backgroundColor === '#E5F8E8' && styles.greenBackground,

        backgroundColor === '#EFECFA' && styles.purpleBackground,
      )}
      moduleRef={containerRef}
      currentModuleBgColor={getModuleBgColor(props)}
      previousModuleBgColor={previousModuleBgColor}
      title={title}
      childrenWithoutHorizontalPadding
    >
      <div className={styles.carouselContainer}>
        <CenteredCarousel
          itemsIdealWidth={780}
          bulletColor="primary"
          onInit={() => {
            setFundsRendered(true);
          }}
        >
          {assetsToShow.map((asset, i) => (
            <React.Fragment key={i}>{renderCard(asset)}</React.Fragment>
          ))}
        </CenteredCarousel>
      </div>
    </ModuleLayout>
  ) : (
    <></>
  );
}

export default AlternativeFundAssetCarouselModule;
